import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  CircularProgress,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DASHBOARD, getDashboardWhiteCardStyles } from "../dashboard/dashboardTheme";
import NetworkFlagDot from "./NetworkFlagDot";
import NetworkFacilityNameCell, {
  type CcnFacilityLocationMap,
  networkFacilityFieldsFromApi,
} from "./NetworkFacilityNameCell";
import type { NetworkRiskFlagsData } from "../../../services/networkDashboard";
import {
  abbreviateQualityMeasureLabel,
  formatNetworkCurrency,
  formatQualityMeasureScore,
  formatVsStateDelta,
} from "../../../services/networkDashboardFormat";
import { compareNetworkTableRows } from "./networkTableSort";
import { getNetworkFacilityTableMaxHeight } from "./networkTableScroll";

type NetworkRiskFacilityRow = NetworkRiskFlagsData["facilities"][number];

const tableSx = {
  width: "100%",
  tableLayout: "fixed",
  "& th": {
    fontWeight: 500,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: DASHBOARD.text,
    borderBottom: `1px solid ${DASHBOARD.border}`,
  },
  "& td": {
    fontSize: "13px",
    color: DASHBOARD.header,
    borderBottom: `1px solid ${DASHBOARD.borderSubtle}`,
    verticalAlign: "top",
  },
  "& tbody tr:hover": { bgcolor: DASHBOARD.tintedCardBg },
};

const facilityColumnSx = {
  width: { xs: "42%", md: "36%" },
  maxWidth: 0,
};

function NetworkScrollableTable({
  rowCount,
  children,
}: {
  rowCount: number;
  children: React.ReactNode;
}) {
  const maxHeight = getNetworkFacilityTableMaxHeight(rowCount);
  const table = (
    <Table size="small" sx={tableSx} stickyHeader={maxHeight != null}>
      {children}
    </Table>
  );

  if (maxHeight == null) return table;

  return (
    <TableContainer
      sx={{
        maxHeight,
        overflowY: "auto",
        "& .MuiTableCell-stickyHeader": { bgcolor: DASHBOARD.cardBg },
      }}
    >
      {table}
    </TableContainer>
  );
}

function PanelShell({
  title,
  subtitle,
  loading,
  headerAction,
  children,
}: {
  title: string;
  subtitle?: string;
  loading?: boolean;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={(theme) => ({
        ...getDashboardWhiteCardStyles(theme),
        mb: 2,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "15px", color: DASHBOARD.header }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {headerAction}
      </Box>
      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: DASHBOARD.accent }} />
          </Box>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}

type NetworkTableColumn = {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  sortable?: boolean;
};

type NetworkTableSort = {
  key: string;
  direction: "asc" | "desc";
};

function renderTableCellValue(
  row: Record<string, unknown>,
  key: string,
  facilityLocations?: CcnFacilityLocationMap,
  facilityNameToCcn?: Record<string, string>,
): React.ReactNode {
  if (key === "provider_name") {
    return (
      <NetworkFacilityNameCell
        facility={row}
        locationByCcn={facilityLocations}
        facilityNameToCcn={facilityNameToCcn}
      />
    );
  }
  const value = row[key];
  return value == null || value === "" ? "—" : String(value);
}

function GenericTablePanel({
  title,
  rows,
  columns,
  loading,
  facilityLocations,
  facilityNameToCcn,
  defaultSort,
}: {
  title: string;
  rows: Record<string, unknown>[];
  columns: NetworkTableColumn[];
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
  defaultSort?: NetworkTableSort;
}) {
  const [sort, setSort] = useState<NetworkTableSort | null>(defaultSort ?? null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => compareNetworkTableRows(a, b, sort.key, sort.direction));
  }, [rows, sort]);

  const handleSort = (key: string) => {
    setSort((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  return (
    <PanelShell title={title} loading={loading}>
      {!loading && rows.length > 0 ? (
        <NetworkScrollableTable rowCount={sortedRows.length}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align ?? "left"}
                  sortDirection={sort?.key === c.key ? sort.direction : false}
                  sx={c.key === "provider_name" ? facilityColumnSx : undefined}
                >
                  {c.sortable ? (
                    <TableSortLabel
                      active={sort?.key === c.key}
                      direction={sort?.key === c.key ? sort.direction : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          opacity: sort?.key === c.key ? 1 : 0.35,
                        },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : (
                    c.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, i) => (
              <TableRow key={String(row.ccn ?? i)}>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align ?? "left"}
                    sx={c.key === "provider_name" ? facilityColumnSx : undefined}
                  >
                    {renderTableCellValue(row, c.key, facilityLocations, facilityNameToCcn)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </NetworkScrollableTable>
      ) : !loading ? (
        <Typography sx={{ color: DASHBOARD.textMuted, fontSize: "13px" }}>No data for this portfolio.</Typography>
      ) : null}
    </PanelShell>
  );
}

function RiskFlagSummaryTile({
  label,
  count,
  tone,
  active,
  onClick,
}: {
  label: string;
  count: number | undefined;
  tone: "red" | "amber";
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        textAlign: "center",
        minWidth: 0,
        cursor: onClick ? "pointer" : "default",
        borderRadius: "8px",
        px: 0.5,
        py: 0.75,
        outline: active ? `2px solid ${tone === "red" ? "#b85c5c" : "#c89234"}` : "none",
        bgcolor: active ? DASHBOARD.tintedCardBg : "transparent",
      }}
    >
      <Typography
        sx={{
          fontSize: "22px",
          fontWeight: 700,
          color: tone === "red" ? "#b85c5c" : "#c89234",
          lineHeight: 1.1,
        }}
      >
        {count ?? 0}
      </Typography>
      <Typography
        sx={{
          mt: 0.9,
          fontSize: "11px",
          color: DASHBOARD.text,
          lineHeight: 1.15,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function RiskFlagValue({ cell }: { cell?: NetworkRiskFacilityRow["flags"][string] }) {
  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <NetworkFlagDot cell={cell} />
      {cell?.value != null && cell.value !== "" ? (
        <Typography sx={{ fontSize: "10px", color: DASHBOARD.text, lineHeight: 1 }}>
          {String(cell.value)}
        </Typography>
      ) : null}
    </Box>
  );
}

function riskBadgeTone(flag: string): { bg: string; color: string } {
  const lower = flag.toLowerCase();
  if (lower.includes("sprinkler")) return { bg: "#f5e6c5", color: "#8a5a10" };
  if (lower.includes("abuse") || lower.includes("sff")) return { bg: "#f1d8d8", color: "#b85c5c" };
  return { bg: DASHBOARD.iconButtonBg, color: DASHBOARD.accent };
}

function mapFacilityTableRow(
  f: Record<string, unknown>,
  metrics: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...networkFacilityFieldsFromApi(f),
    ...metrics,
  };
}

type RiskFlagFilterKey =
  | "sff"
  | "abuse"
  | "survey_age"
  | "ownership"
  | "sprinkler"
  | "family_council"
  | "admin_churn";

const RISK_FLAG_TILES: {
  filter: RiskFlagFilterKey;
  label: string;
  countKey: keyof NetworkRiskFlagsData["distribution"];
  tone: "red" | "amber";
}[] = [
  { filter: "sff", label: "Special Focus Status", countKey: "sff_count", tone: "red" },
  { filter: "abuse", label: "Abuse icon active", countKey: "abuse_count", tone: "red" },
  { filter: "survey_age", label: "Survey > 2 yrs", countKey: "stale_survey_count", tone: "amber" },
  { filter: "ownership", label: "Ownership change < 12mo", countKey: "ownership_churn_count", tone: "amber" },
  { filter: "sprinkler", label: "Sprinkler non-compliant", countKey: "sprinkler_gap_count", tone: "red" },
  { filter: "family_council", label: "No Family Council", countKey: "no_council_count", tone: "amber" },
  { filter: "admin_churn", label: "Admin turnover >= 2", countKey: "high_admin_churn_count", tone: "amber" },
];

export function ExecutiveRiskFlagsPanel({
  data,
  loading,
  facilityLocations,
  facilityNameToCcn,
  activeFlagFilter,
  onFlagFilterChange,
  onFacilitySelect,
  onExport,
  exportLoading,
}: {
  data: NetworkRiskFlagsData | null;
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
  activeFlagFilter?: string | null;
  onFlagFilterChange?: (flag: string | null) => void;
  onFacilitySelect?: (ccn: string) => void;
  onExport?: () => void;
  exportLoading?: boolean;
}) {
  const dist = data?.distribution ?? {};
  const activeTile = RISK_FLAG_TILES.find((tile) => tile.filter === activeFlagFilter);
  const matrixTitle = activeTile
    ? `Risk-flag matrix - ${activeTile.label}`
    : "Risk-flag matrix - all facilities";
  const matrixSubtitle = activeTile
    ? `Showing ${data?.facilities.length ?? 0} of ${data?.total_count ?? 0} facilities with this signal. Click the tile again to clear the filter.`
    : `Showing ${data?.facilities.length ?? 0} of ${data?.total_count ?? 0} facilities. Click any row to drill into that facility's dashboard. Compliant - Warning - Alert`;
  return (
    <>
      {loading || !data ? (
        <PanelShell title="Flag distribution across portfolio" loading={loading}>
          {!loading ? (
            <Typography sx={{ color: DASHBOARD.textMuted, fontSize: "13px" }}>No data for this portfolio.</Typography>
          ) : null}
        </PanelShell>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "2fr 1fr" },
              gap: 2.25,
              mb: 1.5,
              alignItems: "start",
            }}
          >
            <Box
              sx={(theme) => ({
                ...getDashboardWhiteCardStyles(theme),
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              })}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: DASHBOARD.header }}>
                Flag distribution across portfolio
              </Typography>
              <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, mt: 0.5 }}>
                Count of facilities triggering each enforcement / governance signal
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                    lg: "repeat(7, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mt: 2,
                }}
              >
                {RISK_FLAG_TILES.map((tile) => (
                  <RiskFlagSummaryTile
                    key={tile.filter}
                    label={tile.label}
                    count={dist[tile.countKey] as number | undefined}
                    tone={tile.tone}
                    active={activeFlagFilter === tile.filter}
                    onClick={
                      onFlagFilterChange
                        ? () =>
                            onFlagFilterChange(
                              activeFlagFilter === tile.filter ? null : tile.filter,
                            )
                        : undefined
                    }
                  />
                ))}
              </Box>
            </Box>

            <Box
              sx={(theme) => ({
                ...getDashboardWhiteCardStyles(theme),
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              })}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    columnGap: 1,
                    rowGap: 0.25,
                    minWidth: 0,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "15px", color: DASHBOARD.header }}>
                    Highest-risk facilities
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted }}>
                    Composite risk score - top {Math.min(4, data.top_risk_facilities.length)} of {data.total_count}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label="All states"
                  sx={{ bgcolor: DASHBOARD.iconButtonBg, color: DASHBOARD.accent, fontWeight: 500 }}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                {data.top_risk_facilities.slice(0, 4).map((facility) => (
                  <Box
                    key={facility.ccn}
                    onClick={() => onFacilitySelect?.(facility.ccn)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto 28px",
                      gap: 1.5,
                      alignItems: "center",
                      py: 1.35,
                      borderBottom: `1px solid ${DASHBOARD.borderSubtle}`,
                      cursor: onFacilitySelect ? "pointer" : undefined,
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <NetworkFacilityNameCell
                        facility={facility}
                        locationByCcn={facilityLocations}
                        facilityNameToCcn={facilityNameToCcn}
                      />
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end", flexWrap: "wrap" }}>
                      {(facility.primary_flags ?? []).slice(0, 2).map((flag) => {
                        const tone = riskBadgeTone(flag);
                        return (
                          <Chip
                            key={flag}
                            size="small"
                            label={flag}
                            sx={{ height: 22, bgcolor: tone.bg, color: tone.color, fontSize: "11px" }}
                          />
                        );
                      })}
                    </Box>
                    <Typography sx={{ color: "#b85c5c", fontWeight: 700, fontSize: "13px", textAlign: "right" }}>
                      {facility.risk_score}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <PanelShell
            title={matrixTitle}
            subtitle={matrixSubtitle}
            loading={false}
            headerAction={
              onExport ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={onExport}
                  disabled={exportLoading || !data.facilities.length}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    borderColor: DASHBOARD.border,
                    color: DASHBOARD.accent,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {exportLoading ? "Exporting…" : "Export Excel"}
                </Button>
              ) : null
            }
          >
            <NetworkScrollableTable rowCount={data.facilities.length}>
            <TableHead>
              <TableRow>
                <TableCell sx={facilityColumnSx}>Facility</TableCell>
                <TableCell align="center">Risk</TableCell>
                <TableCell align="center">SFF</TableCell>
                <TableCell align="center">Abuse</TableCell>
                <TableCell align="center">Survey</TableCell>
                <TableCell align="center">Ownership</TableCell>
                <TableCell align="center">Sprinkler</TableCell>
                <TableCell align="center">Council</TableCell>
                <TableCell align="center">Admin</TableCell>
                <TableCell align="right" sx={{ width: 24 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.facilities.map((f: NetworkRiskFacilityRow) => (
                <TableRow
                  key={f.ccn}
                  hover
                  sx={{ cursor: onFacilitySelect ? "pointer" : "default" }}
                  onClick={() => onFacilitySelect?.(f.ccn)}
                >
                  <TableCell sx={facilityColumnSx}>
                    <NetworkFacilityNameCell
                      facility={f}
                      locationByCcn={facilityLocations}
                      facilityNameToCcn={facilityNameToCcn}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 700, color: f.risk_score >= 60 ? "#b85c5c" : "#c89234" }}>
                      {f.risk_score}
                    </Typography>
                  </TableCell>
                  {(["sff", "abuse", "survey_age", "ownership", "sprinkler", "family_council", "admin_churn"] as const).map(
                    (k) => (
                      <TableCell key={k} align="center">
                        <RiskFlagValue cell={f.flags[k]} />
                      </TableCell>
                    ),
                  )}
                  <TableCell align="right" sx={{ width: 24, color: DASHBOARD.header }}>
                    {">"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </NetworkScrollableTable>
          </PanelShell>
        </>
      )}
    </>
  );
}

export function RatingsPanel({
  data,
  loading,
  facilityLocations,
  facilityNameToCcn,
}: {
  data: Record<string, unknown> | null;
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
}) {
  const facilities = (data?.facilities as Record<string, unknown>[]) ?? [];
  const rows = facilities.map((f) => {
    const ratings = (f.ratings as Record<string, unknown>) ?? {};
    const deltas = (f.deltas as Record<string, unknown>) ?? {};
    return mapFacilityTableRow(f, {
      overall: ratings.overall,
      health: ratings.health,
      staffing: ratings.staffing,
      qm: ratings.qm,
      vs_state: formatVsStateDelta(deltas.vs_state_overall as number | null | undefined),
    });
  });
  const net = (data?.network_averages as Record<string, unknown>) ?? {};
  return (
    <>
      {!loading && data ? (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: DASHBOARD.tintedCardBg, border: `1px solid ${DASHBOARD.border}` }}>
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.text }}>
            Network averages — Overall: {String(net.overall ?? "—")} · Health: {String(net.health ?? "—")} ·
            Staffing: {String(net.staffing ?? "—")} · QM: {String(net.qm ?? "—")}
          </Typography>
        </Paper>
      ) : null}
      <GenericTablePanel
        title="Facility ratings"
        rows={rows}
        loading={loading}
        facilityLocations={facilityLocations}
        facilityNameToCcn={facilityNameToCcn}
        columns={[
          { key: "provider_name", label: "Facility" },
          { key: "overall", label: "Overall ★", align: "center" },
          { key: "health", label: "Health", align: "center" },
          { key: "staffing", label: "Staffing", align: "center" },
          { key: "qm", label: "QM", align: "center" },
          { key: "vs_state", label: "Δ vs state", align: "right" },
        ]}
      />
    </>
  );
}

export function SurveyPanel({
  data,
  loading,
  facilityLocations,
  facilityNameToCcn,
}: {
  data: Record<string, unknown> | null;
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
}) {
  const tags = (data?.top_tags as Record<string, unknown>[]) ?? [];
  const facilities = (data?.facilities as Record<string, unknown>[]) ?? [];
  return (
    <>
      <PanelShell title="Top deficiency tags" loading={loading}>
        {!loading && tags.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {tags.slice(0, 12).map((t) => (
              <Chip
                key={String(t.tag)}
                label={`${t.tag}: ${t.total_cites} cites / ${t.facility_count} fac`}
                size="small"
                sx={{ fontFamily: "monospace", fontSize: "11px" }}
              />
            ))}
          </Box>
        ) : !loading ? (
          <Typography sx={{ color: DASHBOARD.textMuted, fontSize: "13px" }}>No deficiency events in CMS data.</Typography>
        ) : null}
      </PanelShell>
      <GenericTablePanel
        title="Per-facility survey profile"
        rows={facilities.map((f) =>
          mapFacilityTableRow(f, {
            total_deficiencies: f.total_deficiencies,
            ij_count: f.ij_count,
            g_plus_count: f.g_plus_count,
          }),
        )}
        loading={loading}
        facilityLocations={facilityLocations}
        facilityNameToCcn={facilityNameToCcn}
        columns={[
          { key: "provider_name", label: "Facility" },
          { key: "total_deficiencies", label: "Deficiencies", align: "right" },
          { key: "ij_count", label: "IJ", align: "center" },
          { key: "g_plus_count", label: "G+", align: "center" },
        ]}
      />
    </>
  );
}

export function StaffingPanel({
  data,
  loading,
  facilityLocations,
  facilityNameToCcn,
}: {
  data: Record<string, unknown> | null;
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
}) {
  const facilities = (data?.facilities as Record<string, unknown>[]) ?? [];
  const rows = facilities.map((f) => {
    const st = (f.staffing as Record<string, unknown>) ?? {};
    const to = (f.turnover as Record<string, unknown>) ?? {};
    return mapFacilityTableRow(f, {
      hprd: st.adjusted_total_hprd,
      gap: st.gap,
      rn: st.adjusted_rn_hprd,
      turnover: to.total,
    });
  });
  return (
    <GenericTablePanel
      title="Staffing & workforce"
      rows={rows}
      loading={loading}
      facilityLocations={facilityLocations}
      facilityNameToCcn={facilityNameToCcn}
      columns={[
        { key: "provider_name", label: "Facility" },
        { key: "hprd", label: "Total HPRD", align: "right" },
        { key: "gap", label: "Gap vs state", align: "right" },
        { key: "rn", label: "RN HPRD", align: "right" },
        { key: "turnover", label: "Turnover", align: "right" },
      ]}
    />
  );
}

export function QualityPanel({
  data,
  loading,
  facilityLocations,
  facilityNameToCcn,
}: {
  data: Record<string, unknown> | null;
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
}) {
  const heat = (data?.heatmap as Record<string, unknown>) ?? {};
  const measures =
    (heat.measures as { code: string; label?: string; direction?: string }[]) ?? [];
  const rows = (heat.rows as Record<string, unknown>[]) ?? [];
  return (
    <PanelShell
      title="Quality measure heatmap · 4-quarter avg"
      subtitle="Facility score by clinical quality measure"
      loading={loading}
    >
      {!loading && rows.length > 0 && measures.length > 0 ? (
        <NetworkScrollableTable rowCount={rows.length}>
          <TableHead>
            <TableRow>
              <TableCell sx={facilityColumnSx}>Facility</TableCell>
              {measures.map((m) => (
                <TableCell
                  key={m.code}
                  align="right"
                  sx={{
                    fontSize: "10px",
                    lineHeight: 1.25,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    minWidth: 56,
                  }}
                >
                  {abbreviateQualityMeasureLabel(m.label ?? m.code)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const cells = (r.cells as { measure_code: string; score: number | null }[]) ?? [];
              const cellByCode = new Map(cells.map((c) => [String(c.measure_code), c]));
              return (
                <TableRow key={String(r.ccn)}>
                  <TableCell sx={facilityColumnSx}>
                    <NetworkFacilityNameCell
                      facility={r}
                      locationByCcn={facilityLocations}
                      facilityNameToCcn={facilityNameToCcn}
                    />
                  </TableCell>
                  {measures.map((m) => {
                    const cell = cellByCode.get(String(m.code));
                    return (
                      <TableCell
                        key={m.code}
                        align="right"
                        sx={{
                          fontSize: "12px",
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatQualityMeasureScore(cell?.score)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </NetworkScrollableTable>
      ) : !loading ? (
        <Typography sx={{ color: DASHBOARD.textMuted, fontSize: "13px" }}>No quality measures for this portfolio.</Typography>
      ) : null}
    </PanelShell>
  );
}

export function VbpPanel({
  data,
  loading,
  facilityLocations,
  facilityNameToCcn,
}: {
  data: Record<string, unknown> | null;
  loading: boolean;
  facilityLocations?: CcnFacilityLocationMap;
  facilityNameToCcn?: Record<string, string>;
}) {
  const summary = (data?.summary as Record<string, unknown>) ?? {};
  const dist = (data?.distribution as Record<string, number>) ?? {};
  const facilities = (data?.facilities as Record<string, unknown>[]) ?? [];
  return (
    <>
      {!loading && data ? (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: DASHBOARD.tintedCardBg, border: `1px solid ${DASHBOARD.border}` }}>
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.text }}>
            Net Medicare impact (est.):{" "}
            {typeof summary.net_medicare_impact_estimate === "number"
              ? formatNetworkCurrency(summary.net_medicare_impact_estimate as number)
              : "—"}{" "}
            · Avg performance score: {String(summary.network_avg_performance_score ?? "—")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {Object.entries(dist).map(([k, v]) => (
              <Chip key={k} size="small" label={`${k.replace(/_/g, " ")}: ${v}`} />
            ))}
          </Box>
        </Paper>
      ) : null}
      <GenericTablePanel
        title="Per-facility VBP"
        rows={facilities.map((f) =>
          mapFacilityTableRow(f, {
            performance_score: f.performance_score,
            incentive_payment_multiplier: f.incentive_payment_multiplier,
          }),
        )}
        loading={loading}
        facilityLocations={facilityLocations}
        facilityNameToCcn={facilityNameToCcn}
        defaultSort={{ key: "incentive_payment_multiplier", direction: "asc" }}
        columns={[
          { key: "provider_name", label: "Facility", sortable: true },
          { key: "performance_score", label: "Score", align: "right", sortable: true },
          { key: "incentive_payment_multiplier", label: "Multiplier", align: "right", sortable: true },
        ]}
      />
    </>
  );
}
